// Validated Input component with real-time validation and error display
import React from 'react';
import { RiErrorWarningLine, RiAlertLine } from 'react-icons/ri';

export interface ValidatedInputProps {
  label: string;
  name?: string;
  type?: 'text' | 'email' | 'tel' | 'number' | 'password' | 'url' | 'date' | 'datetime-local';
  value: string | number;
  onChange?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | ((value: string) => void);
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string | null;
  warning?: string | null;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  autoComplete?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const ValidatedInput: React.FC<ValidatedInputProps> = ({
  label,
  name = '',
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  warning,
  required = false,
  disabled = false,
  readOnly = false,
  placeholder,
  className = '',
  inputClassName = '',
  maxLength,
  min,
  max,
  step,
  autoComplete,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...props
}) => {
  const hasError = Boolean(error);
  const hasWarning = Boolean(warning);

  const inputId = `input-${name || label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${name || 'input'}-error`;
  const warningId = `${name || 'input'}-warning`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Simply pass the event
    (onChange as (e: React.ChangeEvent<HTMLInputElement>) => void)(e);
  };

  const getInputClassName = () => {
    let baseClassName = `mt-1.5 block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-all duration-150 ${inputClassName}`;

    if (hasError) {
      baseClassName += ' border-danger-300 text-danger-900 placeholder:text-danger-300 focus:ring-danger-500/20 focus:border-danger-500 bg-danger-50/30';
    } else if (hasWarning) {
      baseClassName += ' border-warning-300 text-warning-900 placeholder:text-warning-300 focus:ring-warning-500/20 focus:border-warning-500 bg-warning-50/30';
    } else {
      baseClassName += ' border-secondary-300 text-secondary-900 placeholder:text-secondary-400 focus:ring-primary-500/20 focus:border-primary-500 bg-white';
    }

    if (disabled) {
      baseClassName += ' bg-secondary-50 text-secondary-400 cursor-not-allowed opacity-75';
    } else if (readOnly) {
      baseClassName += ' bg-secondary-50 text-secondary-600 cursor-default';
    }

    return baseClassName;
  };

  const getAriaDescribedBy = () => {
    const describedBy = [];
    if (ariaDescribedBy) describedBy.push(ariaDescribedBy);
    if (hasError) describedBy.push(errorId);
    if (hasWarning) describedBy.push(warningId);
    return describedBy.length > 0 ? describedBy.join(' ') : undefined;
  };

  return (
    <div className={`${className}`}>
      <label htmlFor={inputId} className="block text-xs font-semibold text-secondary-600 tracking-wide">
        {label}
        {required && <span className="text-danger-500 ml-0.5">*</span>}
      </label>
      
      <div className="relative">
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          // Note: Do NOT use required={required} as it triggers HTML5 native tooltip
          // Use inline error messages instead for consistent UX
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          className={getInputClassName()}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          autoComplete={autoComplete}
          aria-invalid={ariaInvalid || hasError}
          aria-describedby={getAriaDescribedBy()}
          {...props}
        />
        
        {/* Error/Warning Icon */}
        {(hasError || hasWarning) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {hasError ? (
              <RiErrorWarningLine className="h-5 w-5 text-danger-500" />
            ) : (
              <RiAlertLine className="h-5 w-5 text-warning-500" />
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {hasError && (
        <div id={errorId} className="mt-1.5 flex items-center gap-1">
          <RiErrorWarningLine className="h-3.5 w-3.5 text-danger-500 flex-shrink-0" />
          <p className="text-xs text-danger-600">{error}</p>
        </div>
      )}

      {/* Warning Message */}
      {hasWarning && !hasError && (
        <div id={warningId} className="mt-1.5 flex items-center gap-1">
          <RiAlertLine className="h-3.5 w-3.5 text-warning-500 flex-shrink-0" />
          <p className="text-xs text-warning-700">{warning}</p>
        </div>
      )}
    </div>
  );
};

export default ValidatedInput;